import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import {
  getProjectCostSheet,
  createProjectCostSheet,
  updateProjectCostSheet,
  revertProjectCostSheetFromKharash,
  deleteProjectCostSheet
} from '../services/firestoreService';
import { generateProjectCostSheetPDF } from '../services/pdfService';
import { PROJECT_COST_LINE_TEMPLATES, SERVICE_TYPES } from '../utils/constants';
import {
  formatCurrency,
  parseDateInputValue,
  toDateInputValue,
  computeProjectCostTotals,
  normalizeProjectCostLines,
  getProjectCostPdfColumns,
  getServiceTypeLabel,
  formatDate
} from '../utils/helpers';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Loading from '../components/common/Loading';
import RecordAuditBlock from '../components/common/RecordAuditBlock';
import toast from 'react-hot-toast';

const buildDefaultLines = () =>
  PROJECT_COST_LINE_TEMPLATES.map((t, i) => ({
    lineNo: i + 1,
    businessKey: t.value,
    businessName: t.label,
    amount: 0,
    supervisor: '',
    date: '',
    included: false
  }));

const ProjectCostSheetDetail = () => {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const { user } = useAuth();
  const { invoices } = useApp();
  const auditActor = user ? { uid: user.uid, email: user.email } : null;

  const [loading, setLoading] = useState(!isNew);
  const [submitting, setSubmitting] = useState(false);
  const [savedMeta, setSavedMeta] = useState(null);
  const [form, setForm] = useState({
    title: '',
    customerName: '',
    serviceType: '',
    invoiceId: '',
    jobDate: new Date().toISOString().split('T')[0],
    supervisor: '',
    projectAmount: 0,
    lines: buildDefaultLines(),
    postedToKharashAt: null
  });

  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      const result = await getProjectCostSheet(id);
      if (!result.success) {
        toast.error('Lama helin');
        navigate('/admin/project-costs');
        return;
      }
      const s = result.data;
      setSavedMeta(s);
      setForm({
        title: s.title || '',
        customerName: s.customerName || '',
        serviceType: s.serviceType || '',
        invoiceId: s.invoiceId || '',
        jobDate: toDateInputValue(s.jobDate) || new Date().toISOString().split('T')[0],
        supervisor: s.supervisor || '',
        projectAmount: s.projectAmount || 0,
        lines: normalizeProjectCostLines(s.lines?.length ? s.lines : buildDefaultLines()),
        postedToKharashAt: s.postedToKharashAt || null
      });
      setLoading(false);
    })();
  }, [id, isNew, navigate]);

  const totals = useMemo(() => computeProjectCostTotals(form), [form]);

  const pdfColumns = useMemo(
    () => getProjectCostPdfColumns(form, totals.activeLines),
    [form, totals.activeLines]
  );

  const handleLineChange = (index, field, value) => {
    setForm((prev) => {
      const lines = prev.lines.map((line, i) => {
        if (i !== index) return line;
        if (field === 'included') return { ...line, included: Boolean(value) };
        if (field === 'amount') {
          const amount = value;
          return {
            ...line,
            amount,
            included: line.included || parseFloat(amount) > 0
          };
        }
        return { ...line, [field]: value };
      });
      return { ...prev, lines };
    });
  };

  const applyDefaultSupervisorToLines = (supervisor) => {
    setForm((prev) => ({
      ...prev,
      supervisor,
      lines: prev.lines.map((l) => ({ ...l, supervisor: l.supervisor || supervisor }))
    }));
  };

  const handleInvoiceLink = (invoiceId) => {
    const inv = invoices.find((i) => i.id === invoiceId);
    setForm((prev) => ({
      ...prev,
      invoiceId,
      customerName: inv?.customerName || prev.customerName,
      serviceType: inv?.serviceType || prev.serviceType,
      projectAmount: inv?.totalAmount || prev.projectAmount,
      title: inv
        ? `${inv.customerName} · ${SERVICE_TYPES.find((s) => s.value === inv.serviceType)?.label || inv.serviceType}`
        : prev.title
    }));
  };

  const handleSave = async () => {
    setSubmitting(true);
    const normalizedLines = normalizeProjectCostLines(form.lines, { defaultIncludedFromAmount: false });
    const computed = computeProjectCostTotals({ ...form, lines: normalizedLines });
    const payload = {
      title: form.title,
      customerName: form.customerName,
      serviceType: form.serviceType,
      invoiceId: form.invoiceId || null,
      quotationId: null,
      jobDate: parseDateInputValue(form.jobDate),
      supervisor: form.supervisor,
      projectAmount: parseFloat(form.projectAmount) || 0,
      lines: normalizedLines.map((l, i) => ({
        ...l,
        lineNo: i + 1,
        amount: parseFloat(l.amount) || 0,
        included: Boolean(l.included),
        date: l.date ? parseDateInputValue(l.date) : parseDateInputValue(form.jobDate)
      })),
      qarashkaTotal: computed.qarashkaTotal,
      netIncome: computed.netIncome,
      auditActor
    };

    const result = isNew
      ? await createProjectCostSheet(payload)
      : await updateProjectCostSheet(id, payload);

    if (result.success) {
      toast.success('Waa la kaydiyay');
      if (isNew) navigate(`/admin/project-costs/${result.id}`);
      else setSavedMeta((m) => (m ? { ...m, ...payload } : m));
    } else {
      toast.error(result.error);
    }
    setSubmitting(false);
  };

  const handleRevertPost = async () => {
    if (isNew || !form.postedToKharashAt) return;
    if (!window.confirm('Ka noqo post-kii kharash? Kharashyada la sameeyay waa la tirtiri doonaa.')) return;
    setSubmitting(true);
    const result = await revertProjectCostSheetFromKharash(id, auditActor);
    if (result.success) {
      toast.success('Post waa la noqday');
      setForm((prev) => ({ ...prev, postedToKharashAt: null }));
      const refreshed = await getProjectCostSheet(id);
      if (refreshed.success) setSavedMeta(refreshed.data);
    } else {
      toast.error(result.error);
    }
    setSubmitting(false);
  };

  const handlePdf = async () => {
    await generateProjectCostSheetPDF({ ...form, ...totals, id: isNew ? undefined : id });
    toast.success('PDF waa la soo dejiyay');
  };

  const handleDelete = async () => {
    if (isNew || !window.confirm('Tirtir sheet-kan?')) return;
    const result = await deleteProjectCostSheet(id);
    if (result.success) {
      toast.success('Waa la tirtiray');
      navigate('/admin/project-costs');
    }
  };

  if (loading) return <Loading fullScreen />;

  const activeCount = totals.activeLines.length;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex flex-wrap gap-2 justify-between items-center">
        <Button variant="ghost" onClick={() => navigate('/admin/project-costs')}>← Liiska</Button>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handlePdf}>Soo deji PDF</Button>
          {!isNew && form.postedToKharashAt && (
            <Button variant="outline" onClick={handleRevertPost} loading={submitting}>
              Ka noqo kharash
            </Button>
          )}
          <Button onClick={handleSave} loading={submitting}>Kaydi</Button>
          {!isNew && (
            <Button variant="danger" onClick={handleDelete}>Tirtir</Button>
          )}
        </div>
      </div>

      {!isNew && savedMeta && (
        <div className="bg-slate-50 border rounded-xl p-4 text-sm space-y-1">
          <p className="font-semibold text-gray-900">Kayd Firestore</p>
          <p className="text-gray-600">
            {formatDate(savedMeta.updatedAt || savedMeta.createdAt)} · {activeCount} khad PDF ·{' '}
            {form.postedToKharashAt ? 'Posted kharash' : 'Aan weli post gareyn'}
          </p>
          <RecordAuditBlock record={savedMeta} />
        </div>
      )}

      <div className="bg-white rounded-xl border p-4 grid md:grid-cols-2 gap-4">
        <Input label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Input label="Macmiil" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} />
        <Select
          label="Invoice (optional)"
          value={form.invoiceId}
          onChange={(e) => handleInvoiceLink(e.target.value)}
          options={invoices.map((inv) => ({ value: inv.id, label: `${inv.invoiceNumber} — ${inv.customerName}` }))}
          placeholder="Dooro invoice..."
        />
        <Select
          label="Adeeg"
          value={form.serviceType}
          onChange={(e) => setForm({ ...form, serviceType: e.target.value })}
          options={SERVICE_TYPES}
        />
        <Input label="Taariikh" type="date" value={form.jobDate} onChange={(e) => setForm({ ...form, jobDate: e.target.value })} />
        <Input
          label="Supervisor (default)"
          value={form.supervisor}
          onChange={(e) => applyDefaultSupervisorToLines(e.target.value)}
        />
        <Input
          label="Project ($)"
          type="number"
          value={form.projectAmount}
          onChange={(e) => setForm({ ...form, projectAmount: e.target.value })}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600 px-1">
        <p>
          Dooro khadadka (checkbox) — PDF iyo kharash waxay qaadanayaan <strong>kaliya</strong> kuwa la dooray.
        </p>
        <p>
          PDF columns: Business + Price
          {pdfColumns.showDate ? ' + Date' : ''}
          {pdfColumns.showSupervisor ? ' + Supervisor' : ''}
        </p>
      </div>

      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-blue-900 text-white">
            <tr>
              <th className="px-2 py-2 w-10">✓</th>
              <th className="px-2 py-2">NO</th>
              <th className="px-2 py-2 text-left">BUSINESS NAME</th>
              <th className="px-2 py-2">PRICE</th>
              <th className="px-2 py-2">DATE</th>
              <th className="px-2 py-2">SUPERVISOR</th>
            </tr>
          </thead>
          <tbody>
            {form.lines.map((line, index) => {
              const selected = line.included === true || (line.included !== false && parseFloat(line.amount) > 0);
              return (
                <tr
                  key={index}
                  className={`border-t ${selected ? 'bg-white' : 'bg-gray-50/80 opacity-75'}`}
                >
                  <td className="px-2 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={Boolean(line.included)}
                      onChange={(e) => handleLineChange(index, 'included', e.target.checked)}
                      aria-label={`Dooro ${line.businessName}`}
                    />
                  </td>
                  <td className="px-2 py-1 text-center">{line.lineNo || index + 1}</td>
                  <td className="px-2 py-1">{line.businessName}</td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-24 border rounded px-2 py-1"
                      value={line.amount}
                      onChange={(e) => handleLineChange(index, 'amount', e.target.value)}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="date"
                      className="border rounded px-2 py-1"
                      value={line.date || form.jobDate}
                      onChange={(e) => handleLineChange(index, 'date', e.target.value)}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      className="w-full border rounded px-2 py-1"
                      value={line.supervisor || form.supervisor}
                      onChange={(e) => handleLineChange(index, 'supervisor', e.target.value)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {activeCount > 0 && (
        <div className="bg-white rounded-xl border p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Preview — waxa PDF ku bixi doona ({activeCount})</h3>
          <ul className="text-sm space-y-1">
            {totals.activeLines.map((line) => (
              <li key={line.businessKey || line.lineNo} className="flex flex-wrap gap-x-3 text-gray-700">
                <span className="font-medium">{line.businessName}</span>
                <span>{formatCurrency(line.amount)}</span>
                {pdfColumns.showDate && (
                  <span className="text-gray-500">{formatDate(line.date || form.jobDate)}</span>
                )}
                {pdfColumns.showSupervisor && (
                  <span className="text-gray-500">{line.supervisor || form.supervisor || '—'}</span>
                )}
              </li>
            ))}
          </ul>
          {form.serviceType && (
            <p className="text-xs text-gray-500 mt-2">Adeeg: {getServiceTypeLabel(form.serviceType)}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-red-600 text-white rounded-lg p-4 text-center">
          <p className="text-sm">Qarashka (la dooray)</p>
          <p className="text-xl font-bold">{formatCurrency(totals.qarashkaTotal)}</p>
        </div>
        <div className="bg-yellow-300 text-gray-900 rounded-lg p-4 text-center">
          <p className="text-sm">Project</p>
          <p className="text-xl font-bold">{formatCurrency(form.projectAmount)}</p>
        </div>
        <div className="bg-green-600 text-white rounded-lg p-4 text-center">
          <p className="text-sm">Net income</p>
          <p className="text-xl font-bold">{formatCurrency(totals.netIncome)}</p>
        </div>
      </div>
    </div>
  );
};

export default ProjectCostSheetDetail;
